import { Link } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useProductImage } from '../hooks/useProductImage';

const normalizePriceValue = (price) => {
  if (price && typeof price === 'object') {
    if (typeof price.price === 'number') return price.price;
    if (typeof price.min === 'number') return price.min;
    if (typeof price.regular === 'number') return price.regular;
    if (typeof price.value === 'number') return price.value;
  }
  if (typeof price === 'number') return price;
  const parsed = parseFloat(String(price || '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatPrice = (value) => {
  const numeric = normalizePriceValue(value);
  return `£${numeric.toFixed(2)}`;
};

const CartItemRow = ({ item, removeFromCart }) => {
  const { imageUrl } = useProductImage(item);

  return (
    <div className="flex gap-3 py-3 border-b border-slate-100">
      {/* Image */}
      <div className="w-16 h-16 bg-slate-100 rounded-lg flex-shrink-0 overflow-hidden">
        {imageUrl ? (
          <img 
            src={imageUrl} 
            alt={item.name}
            className="w-full h-full object-contain"
            onError={(e) => { e.target.src = '/images/placeholder.svg'; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-2xl">💊</div>
        )}
      </div>
      
      {/* Details */}
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-slate-900 truncate">{item.name}</h4>
        {item.packSize && (
          <p className="text-xs text-slate-500">{item.packSize}</p>
        )}
        <div className="flex items-center justify-between mt-1">
          <p className="text-sm font-semibold text-teal-600">{formatPrice(item.price)}</p>
          <button
            onClick={() => removeFromCart(item.id)}
            className="text-xs text-red-500 hover:text-red-700"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
};

const CartSidebar = ({ isOpen, onClose }) => {
  const { items, removeFromCart, getCartTotal, getCartCount } = useCart();

  const subtotal = getCartTotal();
  const shippingCharge = 0;
  const total = subtotal + shippingCharge;

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black/50 z-50 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Sidebar */}
      <div 
        className={`fixed top-0 right-0 h-full w-[calc(100%-3rem)] max-w-md bg-white z-50 shadow-2xl transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-gradient-to-r from-teal-600 to-teal-500">
          <div className="flex items-center gap-2">
            <span className="text-xl">💊</span>
            <h2 className="text-lg font-bold text-white">PillPointUK</h2>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-white/90 font-medium">Cart ({getCartCount()})</span>
            <button 
              onClick={onClose}
              className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col h-[calc(100%-64px)]">
          {items.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
              <span className="text-5xl mb-4">🛒</span>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Your cart is empty</h3>
              <p className="text-sm text-slate-500 mb-4">Add some products to get started!</p>
              <button 
                onClick={onClose}
                className="text-teal-600 font-medium text-sm hover:underline"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <>
              {/* Items List */}
              <div className="flex-1 overflow-y-auto px-4">
                {items.map(item => (
                  <CartItemRow 
                    key={item.id} 
                    item={item} 
                    removeFromCart={removeFromCart}
                  />
                ))}
              </div>

              {/* Summary & Actions */}
              <div className="border-t border-slate-200 p-4 bg-slate-50">
                {/* Free Shipping Badge */}
                <div className="mb-3 p-3 bg-teal-50 border border-teal-200 rounded-lg">
                  <p className="text-xs text-teal-700 font-medium">🎉 You get <span className="font-bold">FREE Shipping</span> on this order!</p>
                </div>

                {/* Price Summary */}
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="font-medium">{formatPrice(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Shipping</span>
                    {shippingCharge > 0 ? (
                      <span className="font-medium">{formatPrice(shippingCharge)}</span>
                    ) : (
                      <span className="text-teal-600 font-medium">FREE</span>
                    )}
                  </div>
                  <div className="flex justify-between text-base font-bold pt-2 border-t border-slate-200">
                    <span>Total</span>
                    <span className="text-teal-600">{formatPrice(total)}</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Link to="/checkout" onClick={onClose} className="block">
                    <button className="w-full bg-teal-600 hover:bg-teal-700 text-white py-3.5 rounded-xl font-semibold transition-all text-sm shadow-lg shadow-teal-600/20 hover:shadow-xl hover:shadow-teal-600/30 flex items-center justify-center gap-2">
                      <span>Proceed to Checkout</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </button>
                  </Link>
                  <Link to="/cart" onClick={onClose} className="block">
                    <button className="w-full bg-white border-2 border-teal-600 text-teal-600 hover:bg-teal-50 py-3.5 rounded-xl font-semibold transition-all text-sm flex items-center justify-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <span>View Full Cart</span>
                    </button>
                  </Link>
                  <button 
                    onClick={onClose}
                    className="w-full text-slate-500 hover:text-teal-600 py-2 font-medium text-sm transition-colors flex items-center justify-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    <span>Continue Shopping</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default CartSidebar;
